import { useMemo } from 'react';
import { useScopedStorage, useScopedStorageReady } from '@/lib/storage';
import { LocalFamilyMembersStore } from '@/lib/local/LocalFamilyMembersStore';
import { LocalCalendarMemberMappingStore } from '@/lib/local/LocalCalendarMemberMappingStore';
import { CalendarPreferencesManager } from '@/lib/managers/CalendarPreferencesManager';
import { DefaultCalendarManager } from '@/lib/calendar/DefaultCalendarManager';
import { FamilyMemberManager } from '@/lib/family/FamilyMemberManager';
import { RoutePlanStore } from '@/lib/route-plan/RoutePlanStore';
import { RouteGeocoder } from '@/lib/route-plan/RouteGeocoder';

/**
 * Hook to get a scoped LocalFamilyMembersStore instance
 * @returns LocalFamilyMembersStore instance or null if storage not ready
 */
export function useFamilyMembersStore(): LocalFamilyMembersStore | null {
  const storage = useScopedStorage();
  
  return useMemo(() => {
    if (!storage) {
      return null;
    }
    return new LocalFamilyMembersStore(storage);
  }, [storage]);
}

/**
 * Hook to get a scoped LocalCalendarMemberMappingStore instance
 * @returns LocalCalendarMemberMappingStore instance or null if storage not ready
 */
export function useCalendarMemberMappingStore(): LocalCalendarMemberMappingStore | null {
  const storage = useScopedStorage();
  
  return useMemo(() => {
    if (!storage) {
      return null;
    }
    return new LocalCalendarMemberMappingStore(storage);
  }, [storage]);
}

/**
 * Hook to get a scoped CalendarPreferencesManager instance
 * @returns CalendarPreferencesManager instance or null if storage not ready
 */
export function useCalendarPreferencesManager(): CalendarPreferencesManager | null {
  const storage = useScopedStorage();
  
  return useMemo(() => {
    if (!storage) {
      return null;
    }
    return new CalendarPreferencesManager(storage);
  }, [storage]);
}

/**
 * Hook to get a scoped DefaultCalendarManager instance
 * @returns DefaultCalendarManager instance or null if storage not ready
 */
export function useDefaultCalendarManager(): DefaultCalendarManager | null {
  const storage = useScopedStorage();

  return useMemo(() => {
    if (!storage) {
      return null;
    }
    return new DefaultCalendarManager(storage);
  }, [storage]);
}

/**
 * Hook to get a scoped FamilyMemberManager instance
 * @returns FamilyMemberManager instance or null if storage not ready
 */
export function useFamilyMemberManager(): FamilyMemberManager | null {
  const storage = useScopedStorage();
  
  return useMemo(() => {
    if (!storage) {
      return null;
    }
    const familyMembersStore = new LocalFamilyMembersStore(storage);
    const calendarMemberMappingStore = new LocalCalendarMemberMappingStore(storage);
    return new FamilyMemberManager(familyMembersStore, calendarMemberMappingStore);
  }, [storage]);
}

/**
 * Hook to get a scoped RoutePlanStore instance
 * @returns RoutePlanStore instance or null if storage not ready
 */
export function useRoutePlanStore(): RoutePlanStore | null {
  const storage = useScopedStorage();

  return useMemo(() => {
    if (!storage) {
      return null;
    }
    return new RoutePlanStore(storage);
  }, [storage]);
}

/**
 * Hook to get a scoped RouteGeocoder instance
 * @returns RouteGeocoder instance or null if storage not ready
 */
export function useRouteGeocoder(): RouteGeocoder | null {
  const storage = useScopedStorage();

  return useMemo(() => {
    if (!storage) {
      return null;
    }
    return new RouteGeocoder(storage);
  }, [storage]);
}

/**
 * Hook that returns all scoped managers at once
 * @returns Object with all scoped manager instances, or null values if not ready
 */
export function useScopedManagers() {
  const storage = useScopedStorage();
  const isReady = useScopedStorageReady();
  
  return useMemo(() => {
    if (!storage || !isReady) {
      return {
        familyMembersStore: null,
        calendarMemberMappingStore: null,
        calendarPreferencesManager: null,
        familyMemberManager: null,
        isReady: false,
      };
    }
    
    const familyMembersStore = new LocalFamilyMembersStore(storage);
    const calendarMemberMappingStore = new LocalCalendarMemberMappingStore(storage);
    
    return {
      familyMembersStore,
      calendarMemberMappingStore,
      calendarPreferencesManager: new CalendarPreferencesManager(storage),
      familyMemberManager: new FamilyMemberManager(familyMembersStore, calendarMemberMappingStore),
      isReady: true,
    };
  }, [storage, isReady]);
}
